import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useParams } from 'react-router-dom';
import DatePicker, { RangePickerProps } from 'antd/lib/date-picker';
import { Typography } from 'antd';
import { useQuery } from '@apollo/client';
// Types
import {
  GetScheduleEvents,
  GetScheduleEventsVariables,
  GetScheduleEvents_getScheduleEvents,
} from 'api/scheduleEvents/types/GetScheduleEvents';
// Api
import { GET_SCHEDULE_EVENTS } from 'api/scheduleEvents/queries';
// UI
import Table from 'ui/Table';
// Helpers
import { getPublicStreamDate } from 'helpers/utils';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ScheduledEvents = (): JSX.Element => {
  const { storeId } = useParams<{ storeId: string | undefined }>();

  const defaultStartDate = moment()
    .subtract(1, 'months')
    .startOf('day')
    .utc(true);

  const defaultEndDate = moment().endOf('day').utc(true);
  const [startDate, setStartDate] = useState<Moment>(defaultStartDate);
  const [endDate, setEndDate] = useState<Moment>(defaultEndDate);
  const [datePickerStartDate, setDatePickerStartDate] = useState<Moment | null>(
    startDate
  );
  const [datePickerEndDate, setDatePickerEndDate] = useState<Moment | null>(
    endDate
  );

  const { data } = useQuery<GetScheduleEvents, GetScheduleEventsVariables>(
    GET_SCHEDULE_EVENTS,
    {
      variables: {
        input: {
          userId: storeId as string,
          dateRange: {
            from: startDate.toISOString(),
            to: endDate.toISOString(),
          },
        },
      },
      skip: !storeId,
      fetchPolicy: 'cache-and-network',
    }
  );
  const getMaxDate = (current: Moment) =>
    moment().diff(current, 'days', true) <= 0;

  const onDateRangeChange = (start: Moment, end: Moment) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    if (!dates) return null;

    const [start, end] = dates;

    setDatePickerStartDate(start);
    setDatePickerEndDate(end);

    if (start && end) {
      onDateRangeChange(
        moment.parseZone(start).utc(true).startOf('day'),
        moment.parseZone(end).utc(true).endOf('day')
      );
    }
  };

  const startDateColumn = {
    title: 'Start date',
    dataIndex: 'startTs',
    key: 'scheduleDate',
    align: 'center' as const,
    width: 110,
    sorterType: 'date',
    render: (
      id: string,
      record: GetScheduleEvents_getScheduleEvents
    ): JSX.Element => {
      return <>{record.startTs ? getPublicStreamDate(record.startTs) : '-'}</>;
    },
  };

  const endDateColumn = {
    title: 'End date',
    dataIndex: 'startTs',
    key: 'endTs',
    align: 'center' as const,
    width: 110,
    sorterType: 'date',
    render: (
      id: string,
      record: GetScheduleEvents_getScheduleEvents
    ): JSX.Element => {
      return <>{record.endTs ? getPublicStreamDate(record.endTs) : '-'}</>;
    },
  };

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', align: 'left' as const },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left' as const,
      width: 60,
    },
    startDateColumn,
    endDateColumn,
  ];

  return (
    <>
      <div className="payoutsListDatePicker">
        <Text>Select date:</Text>
        <RangePicker
          defaultValue={[datePickerStartDate, datePickerEndDate]}
          disabledDate={getMaxDate}
          onChange={handleDateRangeChange}
        />
      </div>

      <Table
        columns={columns}
        scroll={{ x: 300 }}
        data={data?.getScheduleEvents}
        total={data?.getScheduleEvents.length}
      />
    </>
  );
};

export default ScheduledEvents;
